import type { AnalyticsEvent, TelemetryPreferences } from "./types";

const PROHIBITED_PROPERTY_KEYS = new Set([
  "authorization",
  "clipboard",
  "clipboardcontent",
  "customtext",
  "email",
  "emailaddress",
  "input",
  "keystroke",
  "keystrokes",
  "password",
  "passage",
  "rawcontent",
  "rawtext",
  "sourcetext",
  "token",
  "typedcontent",
  "typedtext",
]);
const EMAIL_LIKE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_PROPERTY_COUNT = 40;
const MAX_STRING_LENGTH = 160;
const MAX_EVENT_HISTORY = 400;

export function normalizeTelemetryPreferences(
  value: unknown,
): TelemetryPreferences {
  if (!value || typeof value !== "object") {
    return { enabled: false };
  }
  return { enabled: Boolean((value as { enabled?: unknown }).enabled) };
}
function normalizedKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z]/g, "");
}

export function sanitizeAnalyticsProperties(
  properties: unknown,
): Record<string, string | number | boolean | null> {
  if (
    !properties ||
    typeof properties !== "object" ||
    Array.isArray(properties)
  ) {
    return {};
  }

  const sanitized: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(properties).slice(
    0,
    MAX_PROPERTY_COUNT,
  )) {
    if (PROHIBITED_PROPERTY_KEYS.has(normalizedKey(key))) {
      continue;
    }
    if (typeof value === "string") {
      if (EMAIL_LIKE.test(value.trim())) {
        continue;
      }
      sanitized[key] = value.slice(0, MAX_STRING_LENGTH);
      continue;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      sanitized[key] = value;
      continue;
    }
    if (typeof value === "boolean" || value === null) {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

export function createAnalyticsEvent(
  eventName: string,
  properties: unknown,
  context: {
    enabled: boolean;
    eventId: string;
    occurredAt: string;
    appVersion: string;
    platform: string;
    viewportClass: string;
    reducedMotion: boolean;
    highContrast: boolean;
    anonymousUserId?: string;
    accountUserId?: string;
    sessionId?: string;
  },
): AnalyticsEvent | null {
  if (!context.enabled || !/^[a-z][a-z0-9_]{1,63}$/.test(eventName)) {
    return null;
  }

  return {
    eventId: context.eventId,
    eventName,
    eventVersion: 1,
    occurredAt: context.occurredAt,
    ...(context.anonymousUserId
      ? { anonymousUserId: context.anonymousUserId }
      : {}),
    ...(context.accountUserId ? { accountUserId: context.accountUserId } : {}),
    ...(context.sessionId ? { sessionId: context.sessionId } : {}),
    appVersion: context.appVersion,
    platform: context.platform,
    viewportClass: context.viewportClass,
    reducedMotion: context.reducedMotion,
    highContrast: context.highContrast,
    properties: sanitizeAnalyticsProperties(properties),
  };
}

export function appendAnalyticsEvent(
  events: readonly AnalyticsEvent[],
  event: AnalyticsEvent | null,
): AnalyticsEvent[] {
  if (!event || events.some((candidate) => candidate.eventId === event.eventId)) {
    return [...events];
  }
  return [...events, event].slice(-MAX_EVENT_HISTORY);
}
